import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { fetchProducts, fetchCategories } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { PRODUCTS } from "../constants/testIds";
import { Input } from "../components/ui/input";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts({ category: category || undefined, q: q || undefined })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q]);

  const setCategory = (slug) => {
    const nextParams = new URLSearchParams(params);
    if (slug) {
      nextParams.set("category", slug);
    } else {
      nextParams.delete("category");
    }
    setParams(nextParams, { replace: true });
  };

  const activeCat = categories.find((c) => c.slug === category);

  return (
    <>
      <section className="bg-brand-navy text-white">
        <div className="section-x mx-auto max-w-[1440px] py-20 md:py-24">
          <div className="overline text-accent">Catalogue</div>
          <h1 className="mt-3 font-display text-5xl md:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">
            {activeCat ? activeCat.name : "Industrial machinery"}
          </h1>
          <p className="mt-5 max-w-2xl text-slate-300 text-lg leading-relaxed">
            {activeCat
              ? activeCat.description
              : "Precision-engineered systems — from CNC machining and packaging lines to robotic cells and process reactors."}
          </p>
        </div>
      </section>

      <section className="section-x mx-auto max-w-[1440px] py-14">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategory("")}
              data-testid={PRODUCTS.categoryFilter("all")}
              className={`h-9 px-4 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors duration-200 ${!category ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-slate-200 hover:border-brand-navy hover:text-brand-navy"}`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(c.slug)}
                data-testid={PRODUCTS.categoryFilter(c.slug)}
                className={`h-9 px-4 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors duration-200 ${category === c.slug ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-slate-200 hover:border-brand-navy hover:text-brand-navy"}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search machines…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid={PRODUCTS.searchInput}
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div data-testid={PRODUCTS.grid} className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
                  <div className="h-5 w-3/4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-3 w-full bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))
          ) : products.length === 0 ? (
            <div className="col-span-full py-20 text-center text-slate-500">No machines match your filter.</div>
          ) : (
            products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)
          )}
        </div>
      </section>
    </>
  );
}
